import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { MoveGroupToSpacePopup } from 'APP/main/PopupManager/Popups/MoveGroupToSpacePopup/MoveGroupToSpacePopup';
import { Features } from 'APP/packages/features/Features';
import { RouterLink } from 'APP/router/constants';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Space } from 'STORE/Spaces/Space';

interface IMoveGroupToSpace {
  group: Group | null;
  space: Space | null;
  spaces: Space[];
  canMoveToSpace: boolean;
  canRemoveFromSpace: boolean;
  moveToSpace(): PopupStore;
  removeFromSpace(): Promise<void>;
}

export function useMoveGroupToSpace(groupId: string | null): IMoveGroupToSpace {
  const navigateTo = useNavigateTo();
  const { t } = useTranslation();
  const source = useAsObservableSource({ groupId, navigateTo });

  const presenter = useLocalStore<IMoveGroupToSpace>(() => ({
    get group(): Group | null {
      return source.groupId ? Entities.GroupsStore.getGroupById(source.groupId) : null;
    },

    get space(): Space | null {
      return presenter.group?.spaceId
        ? Entities.spacesStore.getById(presenter.group.spaceId)
        : null;
    },

    get spaces(): Space[] {
      return Entities.spacesStore.spaces.filter(
        (space) => (space.isOwner || space.isAdmin) && space.id !== presenter.group?.spaceId
      );
    },

    get canMoveToSpace(): boolean {
      return !!(
        Features.isSpacesEnabled() &&
        (presenter.group?.isChatGroup || presenter.group?.isChannel) &&
        presenter.group?.isOwner &&
        presenter.spaces.length
      );
    },

    moveToSpace(): PopupStore {
      return Tasks.app.openPopup(MoveGroupToSpacePopup, {
        params: { groupId: presenter.group!.id },
      });
    },

    get canRemoveFromSpace(): boolean {
      return (
        Features.isSpacesEnabled() &&
        (!!presenter.space?.isOwner || !!(presenter.space?.isAdmin && presenter.group?.isOwner))
      );
    },

    async removeFromSpace(): Promise<void> {
      const canRemove = await Tasks.app.showConfirm({
        text: presenter.group?.isChannel
          ? t('space_remove_channel_alert')
          : t('space_remove_group_alert'),
      });

      if (!canRemove) {
        return;
      }

      const success = await Tasks.space.removeGroupsFromSpace([presenter.group!.id]);

      if (success) {
        Tasks.app.showToast(t('space_alert_removed'));

        if (presenter.group!.isClosed && presenter.group!.withoutMe) {
          Entities.spacesStore.activeSpaceId
            ? source.navigateTo({
                to: RouterLink.Space,
                params: { id: Entities.spacesStore.activeSpaceId },
              })
            : Tasks.group.setActiveGroup({ groupId: null });
        }
      } else {
        Tasks.app.showAlert(t('common_somethings_wrong_error'));
      }
    },
  }));

  return presenter;
}
